import React, { useState, useEffect } from 'react';
import { Shield, Users, UserX, UserCheck, AlertCircle, Loader2, Crown, X, Zap, Gift, Tag, Plus, BarChart3 } from 'lucide-react';
import { getStoredToken } from '../services/auth';
import StatsChart from './StatsChart';

interface User {
  userId: string;
  username: string;
  isAdmin: boolean;
  isPro: boolean;
  isMax: boolean;
  bonusGames?: number;
  bonusPredictions?: number;
}

interface PromoCode {
  code: string;
  bonusGames: number;
  bonusPredictions: number;
  subscriptionType?: 'pro' | 'max';
  maxUses: number;
  currentUses: number;
  createdAt: number;
  createdBy: string;
  isActive: boolean;
}

interface StatsData {
  global: {
    messagesSent: number;
    messagesGenerated: number;
    predictionsGenerated: number;
  };
  daily: {
    dates: string[];
    messagesSent: number[];
    messagesGenerated: number[];
    predictionsGenerated: number[];
  };
}

interface AdminPanelProps {
  token: string;
  onClose: () => void;
}

// Helper to make authenticated requests
function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return new Promise((resolve, reject) => {
    let token: string | null = null;
    
    try {
      token = getStoredToken();
    } catch (e) {
      console.error('Error getting stored token:', e);
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    resolve(
      fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      })
    );
  });
}

export default function AdminPanel({ token, onClose }: AdminPanelProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'promos' | 'stats'>('users');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [promoFormError, setPromoFormError] = useState<string | null>(null);
  
  // Promo code form state
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '',
    bonusGames: 0,
    bonusPredictions: 0,
    subscriptionType: '' as '' | 'pro' | 'max',
    maxUses: 1,
  });

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is admin
      const checkResponse = await authenticatedFetch('/api/admin', {
        method: 'GET',
      });

      if (!checkResponse.ok) {
        throw new Error('Failed to check admin status');
      }

      const checkData = await checkResponse.json();

      if (!checkData.isAdmin) {
        // User is not an admin
        setIsAdmin(false);
        setError('Access denied: You do not have admin privileges.');
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      // Load users list
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin panel');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ action: 'list-users' }),
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleRevokeAdmin = async (userId: string) => {
    if (!confirm('Are you sure you want to revoke admin access from this user?')) {
      return;
    }

    try {
      setActionLoading(userId);
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: 'revoke',
          targetUserId: userId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to revoke admin access');
      }

      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke admin access');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakeAdmin = async (userId: string) => {
    if (!confirm('Are you sure you want to grant admin access to this user?')) {
      return;
    }

    try {
      setActionLoading(userId);
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: 'make-admin',
          targetUserId: userId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to grant admin access');
      }

      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to grant admin access');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePro = async (userId: string, currentProStatus: boolean) => {
    const action = currentProStatus ? 'revoke' : 'grant';
    if (!confirm(`Are you sure you want to ${action} Pro status for this user?`)) {
      return;
    }

    try {
      setActionLoading(userId);
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: currentProStatus ? 'revoke-pro' : 'make-pro',
          targetUserId: userId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to ${action} Pro status`);
      }

      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : `Failed to ${action} Pro status`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleMax = async (userId: string, currentMaxStatus: boolean) => {
    const action = currentMaxStatus ? 'revoke' : 'grant';
    if (!confirm(`Are you sure you want to ${action} Max status for this user?`)) {
      return;
    }

    try {
      setActionLoading(userId);
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: currentMaxStatus ? 'revoke-max' : 'make-max',
          targetUserId: userId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to ${action} Max status`);
      }

      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : `Failed to ${action} Max status`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantBonus = async (userId: string, username: string) => {
    const gamesInput = prompt(`Grant bonus games to ${username}:\n(Enter number of games, 0 to skip)`);
    if (gamesInput === null) return; // Cancelled
    
    const predictionsInput = prompt(`Grant bonus predictions to ${username}:\n(Enter number of predictions, 0 to skip)`);
    if (predictionsInput === null) return; // Cancelled

    const bonusGames = parseInt(gamesInput) || 0;
    const bonusPredictions = parseInt(predictionsInput) || 0;

    if (bonusGames === 0 && bonusPredictions === 0) {
      alert('No bonus usage granted.');
      return;
    }

    try {
      setActionLoading(userId);
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: 'grant-bonus',
          targetUserId: userId,
          bonusGames,
          bonusPredictions,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to grant bonus usage');
      }

      alert(`Granted ${bonusGames} bonus games and ${bonusPredictions} bonus predictions to ${username}`);
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to grant bonus usage');
    } finally {
      setActionLoading(null);
    }
  };

  // Promo code management functions
  const loadPromoCodes = async () => {
    try {
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ action: 'list-promos' }),
      });

      if (!response.ok) {
        throw new Error('Failed to load promo codes');
      }

      const data = await response.json();
      setPromoCodes(data.promoCodes || []);
    } catch (err) {
      console.error('Failed to load promo codes:', err);
    }
  };

  // Stats management functions
  const loadStats = async () => {
    try {
      const response = await authenticatedFetch('/api/admin?action=get-stats', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to load stats');
      }

      const data = await response.json();
      setStats({
        global: data.global,
        daily: data.daily,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleCreatePromoCode = async () => {
    setPromoFormError(null);
    setSuccessMessage(null);

    if (!promoForm.code.trim()) {
      setPromoFormError('Promo code is required');
      return;
    }

    if (promoForm.bonusGames === 0 && promoForm.bonusPredictions === 0 && !promoForm.subscriptionType) {
      setPromoFormError('Must grant at least some bonus games, predictions, or a subscription');
      return;
    }

    try {
      setActionLoading('create-promo');
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: 'create-promo',
          code: promoForm.code,
          bonusGames: promoForm.bonusGames,
          bonusPredictions: promoForm.bonusPredictions,
          subscriptionType: promoForm.subscriptionType || undefined,
          maxUses: promoForm.maxUses,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create promo code');
      }

      setSuccessMessage('Promo code created successfully!');
      setPromoForm({ code: '', bonusGames: 0, bonusPredictions: 0, subscriptionType: '', maxUses: 1 });
      setShowPromoForm(false);
      await loadPromoCodes();
    } catch (err) {
      setPromoFormError(err instanceof Error ? err.message : 'Failed to create promo code');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivatePromoCode = async (code: string) => {
    // Simple inline confirmation - keeping minimal for this action
    if (!confirm(`Are you sure you want to deactivate promo code "${code}"?`)) {
      return;
    }

    try {
      setActionLoading(code);
      const response = await authenticatedFetch('/api/admin', {
        method: 'POST',
        body: JSON.stringify({ 
          action: 'deactivate-promo',
          code,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to deactivate promo code');
      }

      await loadPromoCodes();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to deactivate promo code');
    } finally {
      setActionLoading(null);
    }
  };

  // Load promo codes when switching to promo tab
  useEffect(() => {
    if (isAdmin && activeTab === 'promos' && promoCodes.length === 0) {
      loadPromoCodes();
    }
  }, [isAdmin, activeTab]);

  // Load stats when switching to stats tab
  useEffect(() => {
    if (isAdmin && activeTab === 'stats' && !stats) {
      loadStats();
    }
  }, [isAdmin, activeTab]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-lg p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center gap-3 text-blue-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading admin panel...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="flex items-center gap-3 text-red-400 mb-4">
            <AlertCircle className="w-6 h-6" />
            <span className="font-semibold">Error</span>
          </div>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="flex items-center gap-3 text-yellow-400 mb-4">
            <AlertCircle className="w-6 h-6" />
            <span className="font-semibold">Access Denied</span>
          </div>
          <p className="text-gray-300 mb-6">You do not have admin access.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-400" />
            <h2 className="text-2xl font-bold text-white">Admin Panel</h2>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Users</span>
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'promos'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Tag className="w-4 h-4" />
            <span>Promo Codes</span>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'stats'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Stats</span>
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* Users List */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-gray-400" />
                <h3 className="text-xl font-semibold text-white">Users</h3>
              </div>

              {users.length === 0 ? (
                <p className="text-gray-400">No users found</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                <div
                  key={user.userId}
                  className="flex items-center justify-between bg-gray-800 rounded-lg p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      user.isMax
                        ? 'bg-gradient-to-br from-purple-500 to-pink-600 ring-2 ring-purple-400'
                        : user.isPro 
                        ? 'bg-gradient-to-br from-blue-500 to-purple-600 ring-2 ring-blue-400' 
                        : 'bg-gradient-to-br from-blue-500 to-purple-600'
                    }`}>
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${
                          user.isMax
                            ? 'bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent'
                            : user.isPro 
                            ? 'bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent' 
                            : 'text-white'
                        }`}>
                          {user.username}
                        </p>
                        {user.isMax && (
                          <span className="text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1.5 py-0.5 rounded">
                            MAX
                          </span>
                        )}
                        {user.isPro && !user.isMax && (
                          <span className="text-[10px] font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white px-1.5 py-0.5 rounded">
                            PRO
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">{user.userId}</p>
                      {(user.bonusGames || user.bonusPredictions) && (
                        <p className="text-green-400 text-xs mt-1">
                          🎁 Bonus: {user.bonusGames || 0} games, {user.bonusPredictions || 0} predictions
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {user.isAdmin && (
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium flex items-center gap-1">
                        <Shield className="w-4 h-4" />
                        Admin
                      </span>
                    )}

                    {user.isAdmin ? (
                      <button
                        onClick={() => handleRevokeAdmin(user.userId)}
                        disabled={actionLoading === user.userId}
                        className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {actionLoading === user.userId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserX className="w-4 h-4" />
                        )}
                        Revoke
                      </button>
                    ) : (
                      <button
                        onClick={() => handleMakeAdmin(user.userId)}
                        disabled={actionLoading === user.userId}
                        className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {actionLoading === user.userId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                        Make Admin
                      </button>
                    )}

                    <button
                      onClick={() => handleToggleMax(user.userId, user.isMax)}
                      disabled={actionLoading === user.userId}
                      className={`px-3 py-1 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 ${
                        user.isMax
                          ? 'bg-pink-500/20 hover:bg-pink-500/30 text-pink-400'
                          : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400'
                      }`}
                    >
                      {actionLoading === user.userId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : user.isMax ? (
                        <X className="w-4 h-4" />
                      ) : (
                        <Zap className="w-4 h-4" />
                      )}
                      {user.isMax ? 'Remove Max' : 'Make Max'}
                    </button>

                    <button
                      onClick={() => handleTogglePro(user.userId, user.isPro)}
                      disabled={actionLoading === user.userId}
                      className={`px-3 py-1 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 ${
                        user.isPro
                          ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400'
                          : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                      }`}
                    >
                      {actionLoading === user.userId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : user.isPro ? (
                        <X className="w-4 h-4" />
                      ) : (
                        <Crown className="w-4 h-4" />
                      )}
                      {user.isPro ? 'Remove Pro' : 'Make Pro'}
                    </button>

                    <button
                      onClick={() => handleGrantBonus(user.userId, user.username)}
                      disabled={actionLoading === user.userId}
                      className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      title="Grant bonus usage"
                    >
                      {actionLoading === user.userId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Gift className="w-4 h-4" />
                      )}
                      Grant Bonus
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-semibold text-blue-400 mb-1">Admin Management</p>
              <p className="mb-2">
                You can grant or revoke admin access to users from this panel.
                Only existing admins can manage other users' admin status.
              </p>
              <p className="font-semibold text-purple-400 mb-1">Pro Subscription</p>
              <p className="mb-2">
                Pro users get 8x prediction usage (1600/day) and unlimited chats.
                They have a gradient blue border around their profile picture and "Pro" badge.
              </p>
              <p className="font-semibold text-pink-400 mb-1">Max Subscription</p>
              <p className="mb-2">
                Max users get unlimited predictions and unlimited chats.
                They have a gradient purple-pink border and "Max" badge.
              </p>
              <p className="font-semibold text-green-400 mb-1">Bonus Usage</p>
              <p>
                Grant additional games and predictions to any user. Bonus usage is consumed after daily free usage.
                Users will see their bonus balance in the usage chart.
              </p>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promos' && (
          <>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-gray-400" />
                  <h3 className="text-xl font-semibold text-white">Promo Codes</h3>
                </div>
                <button
                  onClick={() => {
                    setShowPromoForm(!showPromoForm);
                    setPromoFormError(null);
                    setSuccessMessage(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Promo Code</span>
                </button>
              </div>

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm flex items-center justify-between">
                  <span>{successMessage}</span>
                  <button
                    onClick={() => setSuccessMessage(null)}
                    className="text-green-400 hover:text-green-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Create Promo Code Form */}
              {showPromoForm && (
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-semibold text-white mb-4">New Promo Code</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Code</label>
                      <input
                        type="text"
                        value={promoForm.code}
                        onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                        placeholder="EXAMPLE2025"
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Max Uses</label>
                      <input
                        type="number"
                        min="1"
                        value={promoForm.maxUses}
                        onChange={(e) => setPromoForm({ ...promoForm, maxUses: parseInt(e.target.value) || 1 })}
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Bonus Games</label>
                      <input
                        type="number"
                        min="0"
                        value={promoForm.bonusGames}
                        onChange={(e) => setPromoForm({ ...promoForm, bonusGames: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Bonus Predictions</label>
                      <input
                        type="number"
                        min="0"
                        value={promoForm.bonusPredictions}
                        onChange={(e) => setPromoForm({ ...promoForm, bonusPredictions: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-400 mb-1">Subscription Type (Optional)</label>
                      <select
                        value={promoForm.subscriptionType}
                        onChange={(e) => setPromoForm({ ...promoForm, subscriptionType: e.target.value as '' | 'pro' | 'max' })}
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                      >
                        <option value="">None - Bonus only</option>
                        <option value="pro">Pro Subscription</option>
                        <option value="max">Max Subscription</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Grant a subscription tier along with bonuses (if any)
                      </p>
                    </div>
                  </div>
                  
                  {/* Error Message */}
                  {promoFormError && (
                    <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      {promoFormError}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreatePromoCode}
                      disabled={actionLoading === 'create-promo'}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading === 'create-promo' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowPromoForm(false);
                        setPromoFormError(null);
                      }}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Promo Codes List */}
              {promoCodes.length === 0 ? (
                <p className="text-gray-400">No promo codes found</p>
              ) : (
                <div className="space-y-2">
                  {promoCodes.map((promo) => (
                    <div
                      key={promo.code}
                      className={`bg-gray-800 rounded-lg p-4 ${
                        !promo.isActive ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono font-bold text-xl text-white">{promo.code}</span>
                            {!promo.isActive && (
                              <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">
                                Inactive
                              </span>
                            )}
                            {promo.subscriptionType === 'pro' && (
                              <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded font-bold">
                                PRO
                              </span>
                            )}
                            {promo.subscriptionType === 'max' && (
                              <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded font-bold">
                                MAX
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {promo.subscriptionType && (
                              <div className="col-span-2">
                                <span className="text-gray-400">Subscription:</span>{' '}
                                <span className={`font-semibold ${
                                  promo.subscriptionType === 'max' 
                                    ? 'text-purple-400' 
                                    : 'text-blue-400'
                                }`}>
                                  {promo.subscriptionType.toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-400">Bonus Games:</span>{' '}
                              <span className="text-white font-semibold">{promo.bonusGames}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Bonus Predictions:</span>{' '}
                              <span className="text-white font-semibold">{promo.bonusPredictions}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Uses:</span>{' '}
                              <span className="text-white font-semibold">
                                {promo.currentUses} / {promo.maxUses}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-400">Created:</span>{' '}
                              <span className="text-white">
                                {new Date(promo.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          {promo.isActive && (
                            <button
                              onClick={() => handleDeactivatePromoCode(promo.code)}
                              disabled={actionLoading === promo.code}
                              className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                              {actionLoading === promo.code ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                              Deactivate
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold text-orange-400 mb-1">Promo Code Management</p>
                  <p className="mb-2">
                    Create promo codes to grant bonus games, predictions, and/or subscriptions to users.
                    Each code can be used a limited number of times.
                  </p>
                  <p className="mb-2">
                    You can grant Pro or Max subscriptions through promo codes, optionally with bonus usage.
                  </p>
                  <p className="mb-2">
                    Users can redeem codes on the Purchase page. Each user can only redeem a code once.
                  </p>
                  <p>
                    Deactivated codes cannot be redeemed but remain visible for tracking purposes.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-gray-400" />
                <h3 className="text-xl font-semibold text-white">Usage Statistics</h3>
              </div>

              {!stats ? (
                <div className="flex items-center justify-center gap-3 text-blue-400 py-12">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Loading statistics...</span>
                </div>
              ) : (
                <StatsChart global={stats.global} daily={stats.daily} />
              )}
            </div>

            {/* Info */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold text-blue-400 mb-1">Statistics Overview</p>
                  <p className="mb-2">
                    Track global usage statistics for the application. These metrics show:
                  </p>
                  <ul className="list-disc list-inside space-y-1 mb-2">
                    <li><strong>Messages Sent:</strong> Total number of messages sent by users to the AI</li>
                    <li><strong>AI Responses:</strong> Total number of responses generated by the AI</li>
                    <li><strong>Predictions Generated:</strong> Total number of prediction sets generated by the autocorrect system</li>
                  </ul>
                  <p>
                    Charts show the last 7 days of activity to help identify trends and usage patterns.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
